import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeunload } from 'react-beforeunload';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { selectUser } from '../../../features/auth/authSlice';
import {
  useFinishQuizMutation,
  useSendAnswersMutation,
} from '../../../features/exam/certificationApiSlice';
import { selectQuestion } from '../../../features/exam/examSlice';
import {
  InExamCheckStatus,
  ValidationIncident,
} from '../../../features/exam/guard/types';
import { useCallbackPrompt } from '../../../hooks/useCallbackPrompt';
import { Answer, QuizLog } from '../../../types/certifications/Certification';
import { DialogBox } from '../../DialogBox';

type PropsFormQuestion = {
  quizLog: QuizLog;
  finalize: boolean;
  logId: string;
  canFinishExam: boolean;
  validationStatus: InExamCheckStatus;
  monitoringStatusLabel: string;
  openIncident: ValidationIncident | null;
  forceTerminationIncident: ValidationIncident | null;
  remainingFocusAllowance: number;
};

export function FormQuestion({
  quizLog,
  finalize,
  logId,
  canFinishExam,
  validationStatus,
  monitoringStatusLabel,
  openIncident,
  forceTerminationIncident,
  remainingFocusAllowance,
}: PropsFormQuestion) {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const questions = useSelector(selectQuestion);
  const isFinishingRef = useRef(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, string>
  >({});
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [terminationCountdown, setTerminationCountdown] = useState(5);
  const [submittingCountdown, setSubmittingCountdown] = useState(10);
  const [showQuestionMap, setShowQuestionMap] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const [sendAnswers] = useSendAnswersMutation();
  const [finishQuiz] = useFinishQuizMutation();

  const answeredCount = Object.keys(selectedAnswers).length;
  const blankCount = questions.length - answeredCount;
  const answeredProgressPercent =
    questions.length > 0
      ? Math.round((answeredCount / questions.length) * 100)
      : 0;
  const currentQuestion = questions[currentQuestionIndex];

  const questionPayload = useMemo(() => {
    const payloadQuestions: Answer[] = [];

    questions.forEach(question => {
      const selectedAnswer = selectedAnswers[question._id];
      if (!selectedAnswer) return;

      payloadQuestions.push({
        player: user!._id,
        question: question._id,
        answer: [selectedAnswer],
        quiz: quizLog.quiz,
        quiz_log: quizLog._id,
      });
    });

    return payloadQuestions;
  }, [questions, quizLog._id, quizLog.quiz, selectedAnswers, user]);

  const isAutomaticTermination = Boolean(forceTerminationIncident);
  const isValidationBlocked =
    validationStatus === 'critical' || validationStatus === 'terminated';

  async function persistAnswers(nextAnswers: Record<string, string>) {
    const payloadQuestions = questions.reduce<Answer[]>((payload, question) => {
      const answer = nextAnswers[question._id];
      if (!answer) return payload;

      payload.push({
        player: user!._id,
        question: question._id,
        answer: [answer],
        quiz: quizLog.quiz,
        quiz_log: quizLog._id,
      });

      return payload;
    }, []);

    if (!payloadQuestions.length) return;

    try {
      await sendAnswers(payloadQuestions).unwrap();
    } catch {
      // noop: falha de autosave não deve interromper a prova
    }
  }

  async function finalizeExam(force = false) {
    if (isSubmitting || isFinishingRef.current) return;

    if (!force && (!canFinishExam || isValidationBlocked)) {
      toast.error(
        openIncident?.message ??
          'O envio está bloqueado enquanto houver falha crítica de monitoramento.',
      );
      return;
    }

    isFinishingRef.current = true;
    setIsSubmitting(true);
    setShowDialog(false);
    setShowSubmitConfirmation(false);

    const isAutoTermination = Boolean(forceTerminationIncident);
    const minDelay = isAutoTermination
      ? new Promise<void>(resolve => {
          setTimeout(resolve, 10000);
        })
      : Promise.resolve();

    try {
      if (questionPayload.length) {
        await sendAnswers(questionPayload).unwrap();
      }

      await Promise.all([finishQuiz(quizLog._id).unwrap(), minDelay]);
      navigate(`/result/${logId}`);
    } catch (error) {
      isFinishingRef.current = false;
      setIsSubmitting(false);
      toast.error('Não foi possível enviar a prova. Tente novamente.');
      console.error(error);
    }
  }

  useEffect(() => {
    if (!isSubmitting || !isAutomaticTermination) return undefined;

    let remaining = 10;
    setSubmittingCountdown(remaining);

    const interval = setInterval(() => {
      remaining -= 1;
      setSubmittingCountdown(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  useEffect(() => {
    if (finalize) {
      finalizeExam(true).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalize]);

  useEffect(() => {
    if (!forceTerminationIncident) return undefined;

    let remaining = 5;
    setTerminationCountdown(remaining);

    const interval = setInterval(() => {
      remaining -= 1;
      setTerminationCountdown(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        finalizeExam(true).catch(() => undefined);
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceTerminationIncident?.timestamp]);

  const handleChangeQuestion = async (
    event: ChangeEvent<HTMLInputElement>,
    questionId: string,
  ) => {
    const { value } = event.target;
    const nextAnswers = {
      ...selectedAnswers,
      [questionId]: value,
    };

    setSelectedAnswers(nextAnswers);
    await persistAnswers(nextAnswers);
  };

  useBeforeunload(currentEvent => {
    currentEvent.preventDefault();
  });

  const handleSubmitRequest = () => {
    setShowSubmitConfirmation(true);
  };

  const getNavigationButtonClassName = (index: number, answered: boolean) => {
    if (index === currentQuestionIndex) {
      return 'border-blue-500 bg-blue-600 text-white';
    }

    if (answered) {
      return 'border-green-200 bg-green-50 text-green-700';
    }

    return 'border-slate-200 bg-slate-50 text-slate-600';
  };

  const getNavigationButtonLabel = (index: number, answered: boolean) => {
    if (index === currentQuestionIndex) return `Questão ${index + 1}, atual`;
    if (answered) return `Questão ${index + 1}, respondida`;
    return `Questão ${index + 1}, pendente`;
  };

  if (!currentQuestion) return null;

  if (forceTerminationIncident && !isSubmitting) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 px-4 text-white">
        <div className="w-full max-w-lg rounded-3xl border border-red-400/30 bg-slate-900 p-8 text-center shadow-2xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-500/20">
            <span className="text-2xl font-bold text-red-400">!</span>
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.24em] text-red-400">
            Infração {forceTerminationIncident.occurrence}/
            {forceTerminationIncident.limit} — Limite atingido
          </p>
          <h2 className="mt-3 text-2xl font-semibold">
            {forceTerminationIncident.reasonLabel}
          </h2>
          <p className="mt-3 text-sm text-slate-300">
            {forceTerminationIncident.message}
          </p>
          <p className="mt-6 text-sm text-slate-400">
            A prova será encerrada automaticamente em{' '}
            <span className="font-bold text-white">
              {terminationCountdown}s
            </span>
          </p>
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
            <div
              className="h-1.5 rounded-full bg-red-500 transition-all duration-1000"
              style={{ width: `${(terminationCountdown / 5) * 100}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950 px-4 text-white">
        <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900 p-8 text-center shadow-2xl">
          <div
            className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-white"
            role="status"
            aria-label={
              isAutomaticTermination
                ? 'Encerrando prova por segurança'
                : 'Enviando prova'
            }
          />
          <h2 className="mt-5 text-2xl font-semibold">
            {isAutomaticTermination
              ? 'Encerrando prova por segurança'
              : 'Enviando prova'}
          </h2>
          <p className="mt-3 text-sm text-slate-300">
            {isAutomaticTermination
              ? 'O limite de infrações foi atingido. As respostas registradas estão sendo enviadas automaticamente. Não feche esta janela.'
              : 'O envio final acompanha o estado real da solicitação. Não feche esta janela até a navegação para o resultado.'}
          </p>
          {isAutomaticTermination && (
            <>
              <p className="mt-6 text-sm text-slate-400">
                Redirecionando para o resultado em{' '}
                <span className="font-bold text-white">
                  {submittingCountdown}s
                </span>
              </p>
              <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
                <div
                  className="h-1.5 rounded-full bg-white/60 transition-all duration-1000"
                  style={{ width: `${(submittingCountdown / 10) * 100}%` }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <DialogBox
        showDialog={showPrompt}
        // @ts-ignore
        confirmNavigation={() => confirmNavigation(() => finalizeExam(true))}
        // @ts-ignore
        cancelNavigation={cancelNavigation}
      />

      {showSubmitConfirmation && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/60 px-4">
          <div className="w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              Envio da prova
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
              Confirmar envio da prova
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Você respondeu <strong>{answeredCount}</strong> de{' '}
              <strong>{questions.length}</strong> questões.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-sm">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-700">
                Respondidas: <strong>{answeredCount}</strong>
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-700">
                Pendentes: <strong>{blankCount}</strong>
              </span>
            </div>

            {blankCount > 0 && (
              <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Existem <strong>{blankCount}</strong>{' '}
                {blankCount === 1 ? 'questão pendente' : 'questões pendentes'}.
                Você pode enviar assim mesmo.
              </p>
            )}

            {(!canFinishExam || isValidationBlocked) && (
              <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                O envio está bloqueado.{' '}
                {openIncident?.message ?? monitoringStatusLabel} Restam{' '}
                <strong>{remainingFocusAllowance}</strong> infrações antes do
                encerramento automático.
              </p>
            )}

            {canFinishExam && !isValidationBlocked && (
              <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                O envio está liberado. Esta ação encerra a prova e não poderá
                ser revertida.
              </p>
            )}

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowSubmitConfirmation(false)}
                className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Continuar revisando
              </button>
              <button
                type="button"
                onClick={() => finalizeExam(false)}
                disabled={!canFinishExam || isValidationBlocked}
                className={`rounded-xl bg-emerald-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 ${
                  canFinishExam && !isValidationBlocked
                    ? 'cta-guidance cta-guidance-green'
                    : ''
                }`}
              >
                Enviar prova definitivamente
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuestionMap && (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-slate-950/60 px-4 py-4 sm:py-6">
          <div className="mx-auto w-full max-w-5xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Mapa de questões
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  Navegue sem perder o contexto da prova
                </h2>
                <p className="mt-3 text-sm text-slate-600">
                  Abra este painel quando quiser saltar para outra questão.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowQuestionMap(false)}
                className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 sm:text-sm"
              >
                Fechar
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
              <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-blue-700">
                Atual
              </span>
              <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-green-700">
                Respondida
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">
                Pendente
              </span>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <strong className="block text-slate-900">Questão atual</strong>
                {currentQuestionIndex + 1} de {questions.length}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <strong className="block text-slate-900">Respondidas</strong>
                {answeredCount}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <strong className="block text-slate-900">Pendentes</strong>
                {blankCount}
              </div>
            </div>

            <div
              className="mt-5 flex max-h-[50vh] flex-wrap gap-2 overflow-y-auto pr-1"
              data-testid="question-quick-nav"
            >
              {questions.map((question, index) => {
                const answered = Boolean(selectedAnswers[question._id]);

                return (
                  <button
                    type="button"
                    key={question._id}
                    onClick={() => {
                      setCurrentQuestionIndex(index);
                      setShowQuestionMap(false);
                    }}
                    aria-current={
                      index === currentQuestionIndex ? 'page' : undefined
                    }
                    aria-label={getNavigationButtonLabel(index, answered)}
                    className={`min-w-[52px] rounded-xl border px-3 py-2 text-sm font-semibold transition ${getNavigationButtonClassName(
                      index,
                      answered,
                    )}`}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowQuestionMap(false)}
                className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Fechar mapa
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 lg:space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white/95 px-3 py-1.5 shadow-sm backdrop-blur lg:px-4 lg:py-3">
          <div className="space-y-2 lg:hidden">
            <div className="flex items-center justify-between gap-2">
              <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-sm font-semibold text-slate-900">
                Q {currentQuestionIndex + 1}/{questions.length}
              </span>
              <button
                type="button"
                onClick={() => setShowQuestionMap(true)}
                className="cta-guidance cta-guidance-blue rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Mapa
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 text-[11px] font-medium text-slate-600">
              <span>{answeredCount} respondidas</span>
              <span>{blankCount} pendentes</span>
            </div>
          </div>

          <div className="hidden lg:flex lg:flex-col lg:gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 font-semibold text-slate-900">
                Questão {currentQuestionIndex + 1} de {questions.length}
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">
                Progresso: {answeredCount} respondidas · {blankCount} pendentes
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShowQuestionMap(true)}
                className="cta-guidance cta-guidance-blue rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
              >
                Abrir mapa de questões
              </button>
            </div>
          </div>

          <div className="mt-1 lg:mt-3">
            <div
              className="h-1.5 overflow-hidden rounded-full bg-slate-100 lg:h-2"
              role="progressbar"
              aria-label="Progresso de respostas"
              aria-valuemin={0}
              aria-valuemax={questions.length}
              aria-valuenow={answeredCount}
            >
              <div
                data-testid="answered-progress-fill"
                className="h-full rounded-full bg-blue-600 transition-[width] duration-500 ease-out"
                style={{ width: `${answeredProgressPercent}%` }}
              />
            </div>
          </div>
        </div>

        <form
          className="space-y-4 lg:space-y-5"
          onSubmit={event => event.preventDefault()}
        >
          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm lg:p-5">
            <div className="lg:hidden">
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
                  Questão {currentQuestion.questionNumbering}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Enunciado
                </span>
              </div>
              <h2 className="mt-2 text-lg font-semibold leading-snug text-slate-900">
                {currentQuestion.question}
              </h2>
            </div>

            <div className="hidden flex-col gap-4 sm:flex-row sm:items-start lg:flex">
              <div className="inline-flex min-w-[112px] flex-col rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-blue-900 shadow-sm">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">
                  Questão
                </span>
                <span className="mt-2 text-4xl font-bold leading-none tracking-[-0.04em] sm:text-5xl">
                  {currentQuestion.questionNumbering}
                </span>
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Enunciado
                </p>
                <h2 className="mt-2 text-lg font-semibold leading-snug text-slate-900 sm:text-xl xl:text-2xl">
                  {currentQuestion.question}
                </h2>
              </div>
            </div>

            <div className="mt-4 space-y-2.5 lg:mt-6 lg:space-y-3">
              {currentQuestion.choices.map((choice, indexChoice) => {
                const inputId = `${choice.answer}${currentQuestionIndex}${indexChoice}`;

                return (
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-3 py-3 transition lg:px-4 lg:py-3.5 ${
                      selectedAnswers[currentQuestion._id] === choice.answer
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                    }`}
                    htmlFor={inputId}
                    key={inputId}
                  >
                    <input
                      checked={
                        selectedAnswers[currentQuestion._id] === choice.answer
                      }
                      id={inputId}
                      type="radio"
                      value={choice.answer}
                      name={currentQuestion._id}
                      onChange={event => {
                        handleChangeQuestion(event, currentQuestion._id).catch(
                          () => undefined,
                        );
                      }}
                      className="mt-1 h-4 w-4 border-slate-300 text-blue-700 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">
                      <strong className="mr-1 text-slate-900">
                        {choice.choiceNumbering}.
                      </strong>
                      {choice.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  setCurrentQuestionIndex(index => Math.max(index - 1, 0))
                }
                disabled={currentQuestionIndex === 0}
                className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Questão anterior
              </button>
              <button
                type="button"
                onClick={() =>
                  setCurrentQuestionIndex(index =>
                    Math.min(index + 1, questions.length - 1),
                  )
                }
                disabled={currentQuestionIndex === questions.length - 1}
                className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Próxima questão
              </button>
            </div>

            <button
              type="button"
              onClick={handleSubmitRequest}
              className="cta-guidance cta-guidance-amber rounded-xl border border-amber-700 bg-amber-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              Enviar prova
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
